/**
 * WelcomeTour — controller do tour de boas-vindas (coach-marks com driver.js).
 *
 * Montado uma vez, global (App). Lê a fase persistida (welcomeTour.ts) e conduz:
 *  - tutorial  → OnboardingModal (carrossel de boas-vindas, tela cheia) — vem PRIMEIRO;
 *  - invite    → modal-convite ("quer o tour?") — vem DEPOIS do tutorial;
 *  - nav       → coach-mark no ícone de Ajustes (canto sup. direito do dashboard);
 *  - profile   → coach-marks em Ajustes › Minha conta, UM por seção;
 *  - companies → coach-mark em Ajustes › Empresas. O fim limpa o tour.
 *
 * O driver.js só enxerga o DOM da página atual, por isso a fase é persistida e o
 * tour "resume" a cada rota. A lógica de estado é pura (welcomeTour.ts); aqui é
 * só a orquestração visual. Verificação fina é no device (1º-login + auth-gated).
 */
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button3D } from '@/components/ui/button-3d';
import { useAuth } from '@/contexts/AuthContext';
import { OnboardingModal } from '@/components/modals/OnboardingModal';
import {
  getTourPhase,
  setTourPhase,
  clearTour,
  nextTourPhase,
  tourRouteForPhase,
  TOUR_EVENT,
  type TourPhase,
} from '@/lib/welcomeTour';

type CoachStep = {
  selector: string;
  title: string;
  description: string;
  /** Lado/alinhamento do popover (driver.js). Default deixa o driver decidir. */
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
};

/**
 * Passos (âncora + copy) por fase com coach-mark. Em `profile` há um passo por
 * seção da aba — os que não existirem no DOM (ex.: "Empresa" é owner/admin) são
 * filtrados em runtime, então a lista pode ter candidatos condicionais.
 */
const STEP_CANDIDATES: Record<'quote' | 'nav' | 'profile' | 'companies', CoachStep[]> = {
  // O tour de boas-vindas termina AQUI, dentro do formulário de orçamento: três
  // passos que percorrem exatamente o caminho da tarefa (para quem → o quê →
  // pronto). Os rótulos falam da AÇÃO, não da tela.
  quote: [
    {
      selector: '[data-tour="tour-quote-client"]',
      title: '1. Para quem é',
      description:
        'Escreva o nome do cliente. Se ele já estiver salvo, toque em "Escolher dos meus clientes".',
    },
    {
      selector: '[data-tour="tour-quote-items"]',
      title: '2. O que você vai cobrar',
      description:
        'Toque em Serviço ou Produto para escrever na hora, ou em Catálogo para reaproveitar algo que você já cadastrou.',
    },
    {
      selector: '[data-tour="tour-quote-save"]',
      title: '3. Pronto!',
      description:
        'Salve e o orçamento é seu. Depois dá para baixar o PDF e mandar no WhatsApp do cliente.',
      // A barra vive colada no rodapé: força o balão para CIMA, senão ele nasce
      // fora da tela (mesma razão do #21).
      side: 'top',
      align: 'center',
    },
  ],
  nav: [
    {
      selector: '[data-tour="tour-settings-icon"]',
      title: 'Ajustes',
      description:
        'Toque aqui, no canto superior direito, para ajustar os dados da sua conta e das suas empresas.',
      // O ícone fica no canto superior direito: força o popover PARA BAIXO (e
      // alinhado à direita) para não cobrir o próprio ícone que ele aponta.
      side: 'bottom',
      align: 'end',
    },
  ],
  profile: [
    {
      selector: '[data-tour="tour-account"]',
      title: 'Sua conta',
      // O e-mail saiu deste cartão e virou a sublinha do hero quando a tela
      // adotou o desenho oficial — a descrição acompanha o que está ali.
      description:
        'Aqui fica o seu plano. No PRO você libera NFS-e, contratos, estoque e mais.',
    },
    // A ORDEM segue o DOM. O driver.js destaca na ordem desta lista, não na da
    // página: com "Indique e ganhe" no meio (onde ele ficava antes de descer
    // para o fim da tela), o tour saltaria topo → rodapé → meio → rodapé.
    {
      selector: '[data-tour="tour-your-profile"]',
      title: 'Seu perfil',
      description: 'Defina o nome que aparece na saudação da tela inicial.',
    },
    {
      selector: '[data-tour="tour-account-actions"]',
      title: 'Ações da conta',
      description:
        'Trocar a senha, gerenciar sua equipe, sair da conta e a central de ajuda ficam aqui embaixo.',
    },
    {
      selector: '[data-tour="tour-referral"]',
      title: 'Indique e ganhe',
      description: 'Compartilhe seu link: seu amigo ganha orçamentos e você também.',
    },
  ],
  companies: [
    {
      selector: '[data-tour="tour-add-company"]',
      title: 'Sua empresa',
      description:
        'Cadastre sua empresa aqui. É onde você define o regime (MEI) e os dados fiscais para emitir NFS-e.',
    },
  ],
};

type CoachPhase = 'quote' | 'nav' | 'profile' | 'companies';

const isCoachPhase = (p: TourPhase | null): p is CoachPhase =>
  p === 'quote' || p === 'nav' || p === 'profile' || p === 'companies';

function matchesRoute(
  phase: CoachPhase,
  pathname: string,
  searchParams: URLSearchParams,
): boolean {
  if (phase === 'quote') return pathname === '/quotes/new';
  if (phase === 'nav') return pathname === '/dashboard';
  return pathname === '/settings' && searchParams.get('tab') === phase;
}

/**
 * Aguarda um elemento aparecer no DOM (a aba/rota monta de forma assíncrona).
 * Usa setTimeout (não requestAnimationFrame) porque o rAF fica PAUSADO em abas
 * ocultas/headless — a troca de aba mostrava a âncora mas o retry por rAF nunca
 * disparava, e o coach-mark da 2ª aba não subia.
 */
function waitForElement(selector: string, timeoutMs = 4000): Promise<HTMLElement | null> {
  return new Promise((resolve) => {
    const start = Date.now();
    const tick = () => {
      const el = document.querySelector<HTMLElement>(selector);
      if (el) return resolve(el);
      if (Date.now() - start > timeoutMs) return resolve(null);
      setTimeout(tick, 80);
    };
    tick();
  });
}

export function WelcomeTour() {
  const { user } = useAuth();
  const [phase, setPhase] = useState<TourPhase | null>(() => getTourPhase());
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const shownRef = useRef<TourPhase | null>(null);   // fase cujo driver já foi exibido
  const pendingRef = useRef<TourPhase | null>(null); // fase com waitForElement em curso
  const driverRef = useRef<ReturnType<typeof driver> | null>(null);

  // Re-lê a fase a cada mudança (start/transições disparam TOUR_EVENT).
  useEffect(() => {
    const onChange = () => setPhase(getTourPhase());
    window.addEventListener(TOUR_EVENT, onChange);
    return () => window.removeEventListener(TOUR_EVENT, onChange);
  }, []);

  // Coach-marks (nav/profile/companies): navega até a página certa e roda o driver.
  // Idempotente: os refs shown/pending evitam lançar duas vezes e o resolve re-checa
  // a fase atual — não dá para depender de um flag de cleanup por-run.
  useEffect(() => {
    // Tour é de 1º login: só orquestra logado (evita rodar em rota pública se
    // sobrar um phase no localStorage após logout). Resume ao logar de novo.
    if (!user) return;

    if (phase === 'tutorial') {
      // O carrossel (OnboardingModal) é a PRIMEIRA etapa e é renderizado abaixo;
      // garante que ele (e o convite/coach-marks que vêm depois) apareçam no
      // dashboard.
      if (location.pathname !== '/dashboard') navigate('/dashboard');
      return;
    }
    if (phase == null || phase === 'invite') {
      // Fases não-coach: reseta o "já exibido" para permitir a exibição dos marks.
      shownRef.current = null;
      return;
    }
    if (!isCoachPhase(phase)) return;
    const target = phase;

    const advance = () => {
      const n = nextTourPhase(target);
      if (n) setTourPhase(n);
      else clearTour(); // última fase → encerra o tour
    };

    if (!matchesRoute(target, location.pathname, searchParams)) {
      // Navega até a página/aba uma única vez; após exibido não força mais.
      if (shownRef.current !== target) navigate(tourRouteForPhase(target)!);
      return;
    }
    if (shownRef.current === target || pendingRef.current === target) return;
    pendingRef.current = target;

    const candidates = STEP_CANDIDATES[target];
    waitForElement(candidates[0].selector).then((el) => {
      pendingRef.current = null;
      if (getTourPhase() !== target) return; // fase mudou enquanto esperava a âncora
      if (shownRef.current === target) return;
      if (!el) {
        advance(); // âncora não apareceu → não trava o usuário
        return;
      }
      // Só os passos cujas âncoras existem (seções condicionais como "Empresa").
      const steps = candidates.filter((c) => document.querySelector(c.selector));
      const multi = steps.length > 1;
      shownRef.current = target;
      const d = driver({
        allowClose: true,
        stagePadding: 6,
        nextBtnText: 'Próximo',
        prevBtnText: 'Voltar',
        // Fim da cadeia → "Entendi". "Próximo" prometeria um passo que não vem.
        doneBtnText: nextTourPhase(target) ? 'Próximo' : 'Entendi',
        showProgress: multi,
        progressText: '{{current}} de {{total}}',
        showButtons: multi ? ['next', 'previous', 'close'] : ['next', 'close'],
        steps: steps.map((s) => ({
          element: s.selector,
          popover: {
            title: s.title,
            description: s.description,
            ...(s.side ? { side: s.side } : {}),
            ...(s.align ? { align: s.align } : {}),
          },
        })),
        onPrevClick: () => d.movePrevious(),
        onNextClick: () => {
          if (d.isLastStep()) {
            d.destroy();
            advance();
          } else {
            d.moveNext();
          }
        },
        onCloseClick: () => {
          d.destroy();
          clearTour(); // "Pular" encerra o tour (o carrossel já veio antes)
        },
      });
      driverRef.current = d;
      d.drive();
    });
  }, [user, phase, location.pathname, searchParams, navigate]);

  // Garante que o overlay do driver seja removido ao desmontar.
  useEffect(() => () => driverRef.current?.destroy?.(), []);

  return (
    <>
      <Dialog
        open={!!user && phase === 'invite'}
        onOpenChange={(open) => { if (!open) clearTour(); }}
      >
        {/* O convite pede uma TAREFA, não um tour. "Fazer tour" prometia
            assistir; "Criar meu primeiro orçamento" promete sair daqui com algo
            pronto — e é o que os três coach-marks seguintes entregam. */}
        <DialogContent className="sm:max-w-sm">
          <DialogHeader className="text-center sm:text-center">
            <DialogTitle className="text-center">
              Bem-vindo ao Orçamigo!
            </DialogTitle>
            <DialogDescription className="text-center">
              Vamos fazer seu primeiro orçamento agora? Leva menos de um minuto e eu te mostro onde
              fica cada coisa.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 pt-2">
            <Button3D color="gray" className="flex-1" onClick={() => clearTour()}>
              Agora não
            </Button3D>
            <Button3D className="flex-1" onClick={() => setTourPhase('quote')}>
              Criar orçamento
            </Button3D>
          </div>
        </DialogContent>
      </Dialog>

      {/* Tutorial (carrossel, tela cheia) — PRIMEIRA etapa. Grava has_seen_onboarding
          ao concluir/fechar; ao fechar, segue para o modal-convite. */}
      <OnboardingModal
        open={!!user && phase === 'tutorial'}
        onOpenChange={(open) => { if (!open) setTourPhase('invite'); }}
      />
    </>
  );
}
